/**
 * AccordionItem Component (Bootstrap accordion item)
 */
import React, { useId, useMemo } from "react";
import {
  getAccordionButtonClass,
  getAccordionCollapseBaseClass,
  getAccordionItemClass,
  getAccordionToggleButtonProps,
  isEasyblocksEditorMode,
} from "../utils/accordion-shell";
import { getHorizontalContentAlign, getSectionBlockAlignClass, getTextAlignClass } from "../utils/content-alignment";
import type { AccordionItemProps } from "../types";

export function AccordionItem(props: AccordionItemProps) {
  const {
    heading,
    Answer,
    contentAlignClass,
    accordionParentId,
  } = props;

  const resolvedContentAlign = getHorizontalContentAlign(contentAlignClass) ?? "start";

  // In editor mode: always show both header and body, disable collapse
  // On frontend: Bootstrap JS handles accordion behavior
  const isInEditor = isEasyblocksEditorMode();
  const reactId = useId();
  const itemId = useMemo(
    () => `eb-accordion-item-${reactId.replace(/[^a-zA-Z0-9_-]/g, "")}`,
    [reactId]
  );
  const headingId = `${itemId}-heading`;
  const collapseId = `${itemId}-collapse`;

  /**
   * ===== CLASSES (frontend can edit here) =====
   * In editor: always expanded, no collapse classes
   * On frontend: Bootstrap JS controls it via data-bs-toggle
   */
  const classes = {
    root: getAccordionItemClass(),
    header: "accordion-header",
    button: getAccordionButtonClass(isInEditor, false),
    collapse: getAccordionCollapseBaseClass(isInEditor),
    body: "accordion-body",
    answer: [
      "d-flex flex-column gap-4",
      getSectionBlockAlignClass(getTextAlignClass(resolvedContentAlign)),
    ].filter(Boolean).join(" "),
  };

  return (
    <div className={classes.root}>
      <h2 className={classes.header} id={headingId}>
        <button
          className={classes.button}
          type="button"
          aria-expanded={isInEditor ? "true" : "false"}
          aria-controls={collapseId}
          {...getAccordionToggleButtonProps(isInEditor, collapseId)}
        >
          <span className="h4 mb-0">{heading}</span>
        </button>
      </h2>
      <div
        id={collapseId}
        className={classes.collapse}
        aria-labelledby={headingId}
        {...(!isInEditor && {
          "data-bs-parent": accordionParentId ? `#${accordionParentId}` : undefined,
        })}
      >
        <div className={classes.body}>
          <div className={classes.answer}>
            {Answer?.map((item, idx) => {
              const ItemType = item.type as React.ComponentType<any>;
              // eslint-disable-next-line react/no-array-index-key
              return (
                <div key={idx} className="w-100 component-item">
                    <ItemType {...item.props} inheritedContentAlign={resolvedContentAlign} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccordionItem;


